import {
  FormControlSizes,
  LoaderSizes,
  LoaderTalos,
  MixpanelEventSource,
  MixpanelSourceProvider,
  useCallbackRef,
} from '@talos/kyoko';
import { memo, useCallback, useEffect } from 'react';
import { MarketSelectorContextProvider, useMarketSelectorContext } from '../MarketSelectorContext';
import { MarketSelectorSelectionsList } from '../MarketSelectorSelectionsList';
import { MarketSelectorTrigger } from '../MarketSelectorTrigger';
import { MarketSelectorWrapper } from '../styles';
import type { MarketSelectorProps } from '../types';
import { MobileMarketSelectorSheet } from './MobileMarketSelectorSheet';

export const MobileMarketSelector = memo((props: MarketSelectorProps) => (
  <MarketSelectorContextProvider
    isNarrow
    marketSelectorAPI={props.marketSelectorAPI}
    showCredentialsOnlyToggle
    disableAggregations={props.disableAggregations}
    extensions={props.extensions}
  >
    <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
      <InnerMobileMarketSelector {...props} />
    </MixpanelSourceProvider>
  </MarketSelectorContextProvider>
));

function InnerMobileMarketSelector({
  disabled: inputDisabled,
  forceOpen,
  onOpen,
  placeholder = 'Select Aggregation...',
  selectionsListPlaceholder,
}: MarketSelectorProps) {
  const { ready, isOpen, setIsOpen } = useMarketSelectorContext();
  const close = useCallback(() => setIsOpen(false), [setIsOpen]);

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen, setIsOpen]);

  const combinedDisabled = inputDisabled || !ready;
  const handleOpen = useCallbackRef(() => {
    if (combinedDisabled) {
      return;
    }

    setIsOpen(true);
    if (!isOpen) {
      onOpen?.();
    }
  });

  if (!ready) {
    return <LoaderTalos size={LoaderSizes.SMALL} />;
  }

  return (
    <MarketSelectorWrapper data-testid="market-selector">
      <MarketSelectorTrigger
        disabled={combinedDisabled}
        onOpen={handleOpen}
        placeholder={placeholder}
        size={FormControlSizes.Default}
      />
      <MobileMarketSelectorSheet isOpen={isOpen} onClose={close} />
      <MarketSelectorSelectionsList placeholder={selectionsListPlaceholder} />
    </MarketSelectorWrapper>
  );
}
